import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, AlertCircle } from 'lucide-react';
import { serverTimestamp, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { DisputeClassification } from '../types';
import { submitNewDisputeRequest } from '../services/disputeService';
import { getStaffByEmail, Staff } from '../services/staffService';

interface DisputeEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  employeeId: string;
  email: string;
  context: {
    targetTransactionId?: string;
    shiftContext?: { date: string; shiftId: string };
  };
}

export const DisputeEntryModal: React.FC<DisputeEntryModalProps> = ({ isOpen, onClose, tenantId, employeeId, email, context }) => {
  const [classification, setClassification] = useState<DisputeClassification>('ADJUSTMENT_ALLOCATION');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [posId, setPosId] = useState<string>('');

  useEffect(() => {
    const fetchStaff = async () => {
      let staff: Staff | null = null;
      if (context.targetTransactionId) {
        const txRef = doc(db, 'tenants', tenantId, 'master_transactions', context.targetTransactionId);
        const txSnapshot = await getDoc(txRef);
        if (txSnapshot.exists()) {
          const txData = txSnapshot.data();
          if (txData.staffId) {
             const staffRef = collection(db, 'tenants', tenantId, 'staff');
             const q = query(staffRef, where('id', '==', txData.staffId));
             const staffSnapshot = await getDocs(q);
             if (!staffSnapshot.empty) {
               staff = staffSnapshot.docs[0].data() as Staff;
             }
          }
        }
      }
      // Fallback to email
      if (!staff) {
        staff = await getStaffByEmail(tenantId, email);
      }
      if (staff) setPosId(staff.posId);
    };
    if (isOpen) {
      fetchStaff();
    }
  }, [tenantId, email, isOpen, context.targetTransactionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!posId) {
      console.error('Missing posId');
      return;
    }
    setIsSubmitting(true);
    try {
      await submitNewDisputeRequest({
        tenantId,
        employeeId,
        email,
        posId,
        classification,
        notes,
        status: 'PENDING_REVIEW',
        createdAt: serverTimestamp(),
        ...context
      });
      onClose();
    } catch (error) {
      console.error('Failed to submit dispute', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-white p-6 rounded-lg w-full max-w-md"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <AlertCircle size={20} className="text-amber-500" />
                Request Review
              </h2>
              <button onClick={onClose}><X size={20} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Issue Category</label>
                <select
                  value={classification}
                  onChange={(e) => setClassification(e.target.value as DisputeClassification)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="ADJUSTMENT_ALLOCATION">Calculated tip amount seems incorrect</option>
                  <option value="ADJUSTMENT_HOURS">My hours for this shift were recorded incorrectly</option>
                  <option value="DATA_GAP_SHIFT">Entire shift is missing</option>
                  <option value="DATA_GAP_SCHEDULE">I worked but do not appear on the schedule</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Additional Context (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border rounded-md p-2"
                  rows={3}
                  maxLength={250}
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review Request'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
